import type { ShortlistItem } from '@elara/client-core/models/ShortlistItemModel';

import type { MapPoint } from '#ui/GoogleMap.js';

import React, { useMemo } from 'react';

import { useShortlistState, useShortlistUIState, useTripsState } from '#states/hooks.js';
import { EmptyState } from '#ui/EmptyState.js';
import { GoogleMap } from '#ui/GoogleMap.js';

/**
 * Tolerance for considering two locations as the same (in degrees)
 * 0.0001 degrees ≈ 11 meters
 */
const LOCATION_TOLERANCE = 0.0001;

/**
 * Round coordinates to a given precision for deduplication
 */
function roundCoordinate(coord: number, precision: number): number {
  return Math.round(coord / precision) * precision;
}

/**
 * Create a location key from coordinates for grouping
 */
function createLocationKey(lat: number, lng: number): string {
  const roundedLat = roundCoordinate(lat, LOCATION_TOLERANCE);
  const roundedLng = roundCoordinate(lng, LOCATION_TOLERANCE);
  return `${roundedLat},${roundedLng}`;
}

/**
 * Deduplicate items by location, grouping items at the same location
 * Items with addresses (but no coordinates) are grouped separately
 */
function deduplicateLocations(
  items: ShortlistItem[],
): Array<{
  location: { lat: number; lng: number } | undefined;
  address: string | undefined;
  label: string | undefined;
  items: ShortlistItem[];
}> {
  const locationMap = new Map<
    string,
    {
      location: { lat: number; lng: number } | undefined;
      address: string | undefined;
      label: string | undefined;
      items: ShortlistItem[];
    }
  >();

  for (const item of items) {
    const mapPoint = item.mapPoint;

    // Skip items with no location data
    if (!mapPoint) continue;

    let key: string;
    if (mapPoint.latitude && mapPoint.longitude) {
      key = createLocationKey(mapPoint.latitude, mapPoint.longitude);
    } else if (mapPoint.address) {
      // For addresses, use normalized address as key
      key = `address:${mapPoint.address.toLowerCase().trim()}`;
    } else {
      continue;
    }

    const existing = locationMap.get(key);

    if (existing) {
      existing.items.push(item);
    } else {
      locationMap.set(key, {
        location:
          mapPoint.latitude && mapPoint.longitude ? { lat: mapPoint.latitude, lng: mapPoint.longitude } : undefined,
        address: mapPoint.address,
        label: mapPoint.label,
        items: [item],
      });
    }
  }

  return [...locationMap.values()];
}

export function ShortlistMapView(): React.ReactNode {
  const tripsState = useTripsState();
  const shortlistState = useShortlistState();
  const uiState = useShortlistUIState();

  const items = shortlistState.items;

  // Use useMemo to ensure we recalculate when filter or items change
  const filteredItems = useMemo(() => {
    if (!uiState.mapGroupFilter) {
      return items;
    }
    return items.filter((item) => item.groupId === uiState.mapGroupFilter);
  }, [items, uiState.mapGroupFilter]);

  // Filter items to only those with location data (coordinates or address)
  const itemsWithLocations = useMemo(() => {
    return filteredItems.filter((item) => item.hasLocation);
  }, [filteredItems]);

  // Deduplicate locations and group items
  const locationGroups = useMemo(() => {
    return deduplicateLocations(itemsWithLocations);
  }, [itemsWithLocations]);

  // Convert to MapPoint format
  const mapPoints = useMemo<MapPoint[]>(() => {
    const points: MapPoint[] = [];
    for (const [index, group] of locationGroups.entries()) {
      const primaryItem = group.items[0];
      if (!primaryItem || !primaryItem.mapPoint) continue;

      // Model data is the neutral MapPoint; widen to the web MapPoint to attach the DOM onClick.
      const point: MapPoint = primaryItem.mapPoint;

      point.onClick = () => {
        if (primaryItem.type === 'room' && primaryItem.roomOption?.hotelId) {
          tripsState.showGuide('hotel', primaryItem.roomOption.hotelId as string);
        } else if (primaryItem.referenceId) {
          tripsState.showGuide(primaryItem.type, primaryItem.referenceId);
        }
      };

      points.push(point);
    }
    return points;
  }, [locationGroups, tripsState]);

  // Calculate center point from all locations (average of coordinates only)
  const mapCenter = useMemo(() => {
    const groupsWithCoordinates = locationGroups.filter((group) => group.location !== null);
    if (groupsWithCoordinates.length === 0) return undefined;

    const sumLat = groupsWithCoordinates.reduce((sum, group) => sum + (group.location?.lat ?? 0), 0);
    const sumLng = groupsWithCoordinates.reduce((sum, group) => sum + (group.location?.lng ?? 0), 0);
    const count = groupsWithCoordinates.length;

    return { latitude: sumLat / count, longitude: sumLng / count };
  }, [locationGroups]);

  // Empty state when no items have locations
  if (itemsWithLocations.length === 0) {
    return (
      <div className="h-full w-full flex flex-col justify-center items-center p-6">
        <EmptyState
          title="No locations available"
          description="None of your shortlisted items have geographic locations. Switch to board view to see all items."
        />
      </div>
    );
  }

  // Create a stable key that changes when filter or points change to force remount
  // Include mapGroupFilter directly in key to ensure remount when filter changes
  const mapKey = `map-${uiState.mapGroupFilter ?? 'all'}-${mapPoints.length}`;

  return (
    <div className="flex-1 w-full">
      <GoogleMap key={mapKey} points={mapPoints} center={mapCenter} className="w-full h-full" />
    </div>
  );
}
